/**
 * SlideThumbnailImage
 *
 * Shared thumbnail component that loads a slide's thumbnail via the API
 * with in-memory caching. Thumbnails are fetched once per slide and
 * reused across re-renders and re-mounts.
 */

import type React from "react"
import { useState } from "react"

import { ImageOff } from "@/components/icons"
import { cn } from "@/lib/utils"

import { useSlideImageBlob } from "../hooks/use-slide-image-blob"
import { useSlideThumbnail } from "../hooks/use-slide-thumbnail"

function ThumbnailPlaceholder({
  fallbackIcon,
  className,
}: {
  fallbackIcon?: React.ReactNode
  className?: string
}) {
  return (
    <span
      className={cn(
        "bg-muted/30 flex h-full w-full flex-col items-center justify-center",
        className
      )}
    >
      <span className="bg-muted/70 text-muted-foreground/70 border-border/50 flex h-12 w-12 items-center justify-center rounded-lg border">
        {fallbackIcon ?? <ImageOff aria-hidden className="h-6 w-6" />}
      </span>
    </span>
  )
}

export interface SlideThumbnailImageProps {
  slideId: string | undefined
  alt?: string
  className?: string
  /** Icon to show when no image is available. Defaults to ImageOff. */
  fallbackIcon?: React.ReactNode
  /**
   * Pre-signed SAS URL embedded by list/search responses. When provided
   * the hook short-circuits the per-slide ``/thumbnail`` round-trip.
   */
  thumbnailUrl?: string | null
  /**
   * Load the full-resolution slide PNG instead of the small thumbnail.
   * Used by the builder, where slides render large enough that the
   * thumbnail looks blurry. Fetches the image through the auth-aware
   * axios client (the Bearer token doesn't ride on plain ``<img src>``
   * requests in dev), caches the resulting blob URL, and falls back to
   * the small thumbnail while the full image is in flight so there's
   * no skeleton flash. (#3)
   */
  highRes?: boolean
}

export function SlideThumbnailImage({
  slideId,
  alt = "",
  className = "h-full w-full object-cover",
  fallbackIcon,
  thumbnailUrl,
  highRes = false,
}: SlideThumbnailImageProps) {
  const thumbnail = useSlideThumbnail(slideId, thumbnailUrl)
  const highResImage = useSlideImageBlob(highRes ? slideId : undefined)
  const [highResImgError, setHighResImgError] = useState(false)

  // High-res path: prefer the full-res blob URL once it loads; show the
  // small thumbnail in the meantime so the card never blanks out. If the
  // full-res endpoint reports failure OR the img element fails to decode
  // the high-res blob, fall back to the thumbnail's error/url chain.
  let url: string | null
  if (highRes && highResImage.url && !highResImgError) {
    url = highResImage.url
  } else if (highRes) {
    url = thumbnail.url
  } else {
    url = thumbnail.url
  }
  const error = highRes ? highResImage.error && thumbnail.error : thumbnail.error

  // When showing the high-res URL, track its decode failures separately
  // so we can fall back to the thumbnail without marking the thumbnail as
  // failed. When showing the thumbnail URL, use its error handler.
  const onImgError =
    highRes && highResImage.url && !highResImgError
      ? () => setHighResImgError(true)
      : thumbnail.onImgError

  // No slide id provided — caller error, render broken-image immediately.
  if (!slideId) {
    return <ThumbnailPlaceholder fallbackIcon={fallbackIcon} />
  }

  // Backend reported missing (render_failed, tenant mismatch, etc.) OR the
  // <img> failed to load the resolved url. Either way: broken-image.
  if (error) {
    return <ThumbnailPlaceholder fallbackIcon={fallbackIcon} />
  }

  // Anything else with no url is in-flight — the hook either has a batch
  // prefetch running or is about to trigger one via its useEffect.
  if (!url) {
    return <span aria-hidden="true" className="skeleton-warm block h-full w-full rounded-none" />
  }

  return <img alt={alt} className={className} loading="lazy" src={url} onError={onImgError} />
}
